import { useState, forwardRef, type ReactNode, type MouseEvent } from 'react';

export type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'link';
export type ButtonTone    = 'cyan' | 'amber' | 'danger' | 'muted' | 'neutral' | 'yellow' | 'green';
export type ButtonSize    = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps {
  children: ReactNode;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  align?: 'center' | 'left';
  /** xs only: overrides vertical padding component (default '2px') */
  py?: string;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit';
}

// ── Colour tables ─────────────────────────────────────────────────────────────

type Clr = { bg: string; color: string; border: string };

const SOLID: Record<ButtonTone, Clr> = {
  cyan:    { bg: 'var(--evo-cyan)',    color: 'var(--evo-fg-on-cyan)', border: 'transparent' },
  amber:   { bg: 'var(--evo-amber)',   color: 'var(--evo-bg)',         border: 'transparent' },
  yellow:  { bg: '#eab308',           color: '#0a0a0a',               border: 'transparent' },
  danger:  { bg: 'var(--evo-danger)',  color: '#fff',                  border: 'transparent' },
  muted:   { bg: 'var(--evo-bg-3)',    color: 'var(--evo-fg-1)',       border: 'transparent' },
  neutral: { bg: 'var(--evo-fg-1)',    color: 'var(--evo-bg)',         border: 'transparent' },
  green:   { bg: 'var(--evo-green-300)', color: '#0a0a0a',             border: 'transparent' },
};

const OUTLINE: Record<ButtonTone, { base: Clr; hover: Clr }> = {
  cyan:    { base: { bg: 'transparent', color: 'var(--evo-cyan)',    border: 'var(--evo-cyan)'    },
             hover: { bg: 'var(--evo-cyan)',           color: 'var(--evo-fg-on-cyan)', border: 'var(--evo-cyan)'    } },
  amber:   { base: { bg: 'transparent', color: 'var(--evo-amber)',   border: 'var(--evo-amber)'   },
             hover: { bg: 'rgba(245,158,11,0.08)',     color: 'var(--evo-amber)',       border: 'var(--evo-amber)'   } },
  yellow:  { base: { bg: 'transparent', color: '#eab308',            border: '#eab308'            },
             hover: { bg: 'rgba(234,179,8,0.08)',      color: '#eab308',               border: '#eab308'            } },
  danger:  { base: { bg: 'transparent', color: 'var(--evo-danger)',  border: 'var(--evo-danger)'  },
             hover: { bg: 'rgba(239,68,68,0.08)',      color: 'var(--evo-danger)',      border: 'var(--evo-danger)'  } },
  muted:   { base: { bg: 'transparent', color: 'var(--evo-fg-2)',    border: 'var(--evo-stroke)'  },
             hover: { bg: 'var(--evo-panel)',           color: 'var(--evo-fg-2)',        border: 'var(--evo-steel)'   } },
  neutral: { base: { bg: 'transparent', color: 'var(--evo-fg-2)',    border: 'var(--evo-stroke)'  },
             hover: { bg: 'var(--evo-panel)',           color: 'var(--evo-fg-1)',        border: 'var(--evo-stroke)'  } },
  green:   { base: { bg: 'transparent', color: 'var(--evo-green-300)', border: 'color-mix(in srgb, var(--evo-green-300) 25%, transparent)' },
             hover: { bg: 'color-mix(in srgb, var(--evo-green-300) 10%, transparent)', color: 'var(--evo-green-300)', border: 'color-mix(in srgb, var(--evo-green-300) 40%, transparent)' } },
};

const GHOST_CLR: Record<ButtonTone, string> = {
  cyan: 'var(--evo-cyan)', amber: 'var(--evo-amber)', yellow: '#eab308',
  danger: 'var(--evo-danger)', muted: 'var(--evo-fg-3)', neutral: 'var(--evo-fg-2)',
  green: 'var(--evo-green-300)',
};

// xs: height auto, padding-based, r-sm, fs-caption, allows text wrap
const H:      Record<ButtonSize, string> = { xs: 'auto', sm: '28px', md: '32px', lg: '40px' };
const PX:     Record<ButtonSize, string> = { xs: '8px',  sm: '12px', md: '16px', lg: '20px' };
const FS:     Record<ButtonSize, string> = { xs: 'var(--evo-fs-caption)', sm: '12px', md: '13px', lg: '14px' };
const GAP:    Record<ButtonSize, number> = { xs: 4, sm: 5, md: 6, lg: 7 };
const ICN:    Record<ButtonSize, number> = { xs: 10, sm: 11, md: 12, lg: 14 };
const RADIUS: Record<ButtonSize, string> = { xs: 'var(--evo-r-sm)', sm: 'var(--evo-r)', md: 'var(--evo-r)', lg: 'var(--evo-r)' };

// ── Spinner ───────────────────────────────────────────────────────────────────

function Spinner({ sz }: { sz: number }) {
  return (
    <svg
      width={sz} height={sz} viewBox="0 0 16 16" fill="none"
      style={{ animation: 'evo-spin 0.7s linear infinite', flexShrink: 0 }}
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  children,
  icon,
  iconAfter,
  variant = 'solid',
  tone = 'cyan',
  size = 'md',
  align = 'center',
  py,
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
}, ref) {
  const [hovered, setHovered] = useState(false);
  const inactive = disabled || loading;
  const h = hovered && !inactive;

  let clr: Clr;
  let opacity = inactive ? 0.5 : 1;
  let textDecoration = 'none';

  if (variant === 'solid') {
    clr = SOLID[tone];
    if (h) opacity = 0.88;
  } else if (variant === 'outline') {
    clr = h ? OUTLINE[tone].hover : OUTLINE[tone].base;
  } else if (variant === 'ghost') {
    clr = { bg: h ? 'var(--evo-bg-3)' : 'transparent', color: GHOST_CLR[tone], border: 'transparent' };
  } else {
    clr = { bg: 'transparent', color: GHOST_CLR[tone], border: 'transparent' };
    if (h) textDecoration = 'underline';
  }

  const isLink = variant === 'link';
  const isXs   = size === 'xs';
  const iconSz = ICN[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onClick={!loading ? onClick : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: align === 'left' || isLink ? 'flex-start' : 'center',
        gap: GAP[size],
        height: isLink ? 'auto' : H[size],
        padding: isLink ? '0' : isXs ? `${py ?? '2px'} ${PX[size]}` : `0 ${PX[size]}`,
        fontSize: FS[size],
        fontWeight: 500,
        fontFamily: 'var(--evo-font)',
        lineHeight: isXs ? 1.35 : 1,
        background: clr.bg,
        color: clr.color,
        border: clr.border === 'transparent' ? '1px solid transparent' : `1px solid ${clr.border}`,
        borderRadius: isLink ? 0 : RADIUS[size],
        cursor: loading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
        opacity,
        textDecoration,
        textAlign: align === 'left' ? 'left' : 'center',
        whiteSpace: isXs ? 'normal' : 'nowrap',
        userSelect: 'none',
        flexShrink: 0,
        width: fullWidth ? '100%' : 'auto',
        transition: 'opacity 120ms linear, background 120ms linear, color 120ms linear, border-color 120ms linear',
        boxSizing: 'border-box',
        outline: 'none',
      }}
    >
      {loading ? (
        <Spinner sz={iconSz} />
      ) : icon != null ? (
        <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>{icon}</span>
      ) : null}
      <span>{children}</span>
      {!loading && iconAfter != null && (
        <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>{iconAfter}</span>
      )}
    </button>
  );
});
